import { useSyncExternalStore } from 'react';

import type { PaymentMode } from '@/api/types';
import type { CartLine } from '@/features/billing/pricing';

interface CartState {
  items: CartLine[];
  discount: number;
  customerName: string;
  customerPhone: string;
  paymentMode: PaymentMode;
}

/**
 * The live cart, as an external store (same pattern as the toast queue —
 * no state library needed for one object).
 *
 * Lives outside React deliberately: a barcode scan, a voice command, and a
 * click on a search result all need to reach the same cart from different
 * parts of the tree without threading a setter through props.
 */
let state: CartState = {
  items: [],
  discount: 0,
  customerName: '',
  customerPhone: '',
  paymentMode: 'Cash',
};

const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return state;
}

function setState(patch: Partial<CartState>) {
  state = { ...state, ...patch };
  emit();
}

export const cartActions = {
  /** Adds one unit, or increments an existing line up to stock on hand. */
  addItem(line: Omit<CartLine, 'qty'>) {
    const existing = state.items.find((item) => item.medicineId === line.medicineId);

    if (existing) {
      if (existing.qty >= existing.maxStock) return false;
      setState({
        items: state.items.map((item) =>
          item.medicineId === line.medicineId ? { ...item, qty: item.qty + 1 } : item,
        ),
      });
      return true;
    }

    setState({ items: [...state.items, { ...line, qty: 1 }] });
    return true;
  },

  changeQty(medicineId: string, delta: number) {
    const items = state.items
      .map((item) => {
        if (item.medicineId !== medicineId) return item;
        const qty = Math.min(item.qty + delta, item.maxStock);
        return { ...item, qty };
      })
      .filter((item) => item.qty > 0);

    setState({ items });
  },

  setQty(medicineId: string, qty: number) {
    if (!Number.isFinite(qty) || qty < 1) return;

    setState({
      items: state.items.map((item) =>
        item.medicineId === medicineId
          ? { ...item, qty: Math.min(Math.floor(qty), item.maxStock) }
          : item,
      ),
    });
  },

  removeItem(medicineId: string) {
    setState({ items: state.items.filter((item) => item.medicineId !== medicineId) });
  },

  setDiscount(discount: number) {
    setState({ discount: Number.isFinite(discount) && discount >= 0 ? discount : 0 });
  },

  setCustomer(name: string, phone: string) {
    setState({ customerName: name, customerPhone: phone });
  },

  setPaymentMode(paymentMode: PaymentMode) {
    setState({ paymentMode });
  },

  /** Called after a successful checkout, or when the pharmacist starts a fresh bill. */
  clear() {
    setState({
      items: [],
      discount: 0,
      customerName: '',
      customerPhone: '',
      // Payment mode carries over — most days run entirely on one till mode.
    });
  },
};

export function useCart(): CartState {
  return useSyncExternalStore(subscribe, getSnapshot);
}
